import React from 'react';
import { Info, FileText, CheckCircle2, XCircle, Sparkles, Database } from './icons';

export function SyntaxItem({ item }) {

  const getBadgeClass = (badge) => {
    const b = (badge || '').toUpperCase();
    if (b.includes('VAR')) return 'badge-variable';
    if (b.includes('COND')) return 'badge-condition';
    if (b.includes('OPT')) return 'badge-optional';
    if (b.includes('CLEAR')) return 'badge-clear';
    if (b.includes('DOLLAR')) return 'badge-dollar';
    if (b.includes('TABLE')) return 'badge-table';
    if (b.includes('ALIGN')) return 'badge-align';
    if (b.includes('STYLE') || b.includes('HEAD') || b.includes('LIST') || b.includes('BOLD')) return 'badge-style';
    if (b.includes('IMAGE')) return 'badge-image';
    if (b.includes('PAGE')) return 'badge-break';
    if (b.includes('LINE')) return 'badge-line';
    if (b.includes('SIGN') || b.includes('WHITE')) return 'badge-signature';
    return 'badge-default';
  };

  return (
    <article className="syntax-item-card" id={`syntax-${item.id}`}>
      {/* ── Item Header ── */}
      <div className="syntax-item-header">
        <h3 className="syntax-item-title">{item.title}</h3>
        {item.badge && (
          <span className={`syntax-badge ${getBadgeClass(item.badge)}`}>
            {item.badge}
          </span>
        )}
      </div>

      {/* ── 1. What It Means ── */}
      {item.meaning && (
        <div className="syntax-sub-section">
          <span className="syntax-sub-label">WHAT IT MEANS</span>
          <p className="syntax-meaning-text">{item.meaning}</p>
        </div>
      )}

      {/* ── 2. Syntax Section ── */}
      <div className="syntax-sub-section">
        <div className="syntax-sub-header">
          <span className="syntax-sub-label">SYNTAX</span>
        </div>
        <pre className="syntax-code-box">
          <code>{item.syntax}</code>
        </pre>
      </div>

      {/* ── 3. Example Section ── */}
      {item.example && (
        <div className="syntax-sub-section">
          <div className="syntax-sub-header">
            <span className="syntax-sub-label">EXAMPLE</span>
          </div>
          <pre className="syntax-code-box syntax-example-code-box">
            <code>{item.example}</code>
          </pre>
        </div>
      )}

      {/* ── 4. Sample Data Section (if present) ── */}
      {item.data && (
        <div className="syntax-sub-section">
          <div className="syntax-sub-header">
            <span className="syntax-sub-label">SAMPLE DATA</span>
            <span className="syntax-data-badge">
              <Database size={11} />
              Input Values
            </span>
          </div>
          <pre className="syntax-code-box syntax-data-code-box">
            <code>{item.data}</code>
          </pre>
        </div>
      )}

      {/* ── Supported Operators (if any) ── */}
      {item.supportedOperators && item.supportedOperators.length > 0 && (
        <div className="syntax-sub-section">
          <span className="syntax-sub-label">SUPPORTED OPERATORS</span>
          <div className="syntax-operators-table-wrapper">
            <table className="syntax-operators-table">
              <thead>
                <tr>
                  <th>Operator</th>
                  <th>Meaning</th>
                  <th>Example</th>
                </tr>
              </thead>
              <tbody>
                {item.supportedOperators.map((op, idx) => (
                  <tr key={idx}>
                    <td className="syntax-op-name">{op.operator}</td>
                    <td className="syntax-op-meaning">{op.meaning}</td>
                    <td className="syntax-op-example"><code>{op.example}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── 5. Generated Output Section (Document-Style Preview Box) ── */}
      {item.sampleOutput && (
        <div className="syntax-sub-section">
          <div className="syntax-sub-header">
            <span className="syntax-sub-label">GENERATED OUTPUT</span>
            <span className="syntax-output-badge">
              <FileText size={11} />
              Rendered Result
            </span>
          </div>

          <div className="syntax-sample-output-wrapper">
            {/* Simple / Single Output Sheet */}
            {(item.sampleOutput.type === 'variable_preview' || item.sampleOutput.type === 'simple_output') && (
              <div className="syntax-output-document-sheet">
                <div className="syntax-output-sheet-header">
                  <span className="syntax-output-sheet-title">
                    {item.sampleOutput.title || 'GENERATED OUTPUT'}
                  </span>
                </div>
                <div className="syntax-output-sheet-body">
                  <pre className="syntax-output-rendered-text">{item.sampleOutput.rendered}</pre>
                </div>
              </div>
            )}

            {/* Conditional Dual (When True vs When False / Removed) */}
            {item.sampleOutput.type === 'conditional_dual' && (
              <div className="syntax-output-cases-grid">
                <div className="syntax-output-case-card syntax-output-case-true">
                  <div className="syntax-output-case-header">
                    <CheckCircle2 size={13} className="text-emerald" />
                    <span>{item.sampleOutput.whenTrueLabel || 'Condition = TRUE'}</span>
                  </div>
                  <div className="syntax-output-case-body">
                    <pre className="syntax-output-case-text">{item.sampleOutput.whenTrue}</pre>
                  </div>
                </div>

                <div className="syntax-output-case-card syntax-output-case-false">
                  <div className="syntax-output-case-header">
                    <XCircle size={13} className="text-rose" />
                    <span>{item.sampleOutput.whenFalseLabel || 'Condition = FALSE'}</span>
                  </div>
                  <div className="syntax-output-case-body">
                    <div className="syntax-output-hidden-indicator">
                      {item.sampleOutput.whenFalse || '[Statement is removed]'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* AND vs OR Dual */}
            {item.sampleOutput.type === 'and_or_dual' && (
              <div className="syntax-output-cases-grid">
                <div className="syntax-output-case-card">
                  <div className="syntax-output-case-header">
                    <Sparkles size={13} className="text-blue" />
                    <span>{item.sampleOutput.andTitle || '&& (AND Expression)'}</span>
                  </div>
                  <div className="syntax-output-case-body">
                    <p className="syntax-output-case-text">{item.sampleOutput.andResult}</p>
                  </div>
                </div>

                <div className="syntax-output-case-card">
                  <div className="syntax-output-case-header">
                    <Sparkles size={13} className="text-indigo" />
                    <span>{item.sampleOutput.orTitle || '|| (OR Expression)'}</span>
                  </div>
                  <div className="syntax-output-case-body">
                    <p className="syntax-output-case-text">{item.sampleOutput.orResult}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Optional vs Condition */}
            {item.sampleOutput.type === 'optional_vs_condition' && (
              <div className="syntax-output-cases-grid">
                <div className="syntax-output-case-card">
                  <div className="syntax-output-case-header">
                    <span className="syntax-badge badge-optional" style={{ margin: 0 }}>{item.sampleOutput.optTitle}</span>
                  </div>
                  <div className="syntax-output-case-body">
                    <p className="syntax-output-case-text">{item.sampleOutput.optDesc}</p>
                  </div>
                </div>

                <div className="syntax-output-case-card">
                  <div className="syntax-output-case-header">
                    <span className="syntax-badge badge-condition" style={{ margin: 0 }}>{item.sampleOutput.condTitle}</span>
                  </div>
                  <div className="syntax-output-case-body">
                    <p className="syntax-output-case-text">{item.sampleOutput.condDesc}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Table Condition Dual */}
            {item.sampleOutput.type === 'table_condition_dual' && (
              <div className="syntax-output-cases-grid">
                <div className="syntax-output-case-card syntax-output-case-true">
                  <div className="syntax-output-case-header">
                    <CheckCircle2 size={13} className="text-emerald" />
                    <span>{item.sampleOutput.whenTrueTitle || 'Condition = TRUE (Row Generated)'}</span>
                  </div>
                  <div
                    className="syntax-output-case-body"
                    dangerouslySetInnerHTML={{ __html: item.sampleOutput.whenTrueHtml }}
                  />
                </div>

                <div className="syntax-output-case-card syntax-output-case-false">
                  <div className="syntax-output-case-header">
                    <XCircle size={13} className="text-rose" />
                    <span>{item.sampleOutput.whenFalseTitle || 'Condition = FALSE (Row Completely Removed)'}</span>
                  </div>
                  <div
                    className="syntax-output-case-body"
                    dangerouslySetInnerHTML={{ __html: item.sampleOutput.whenFalseHtml }}
                  />
                </div>
              </div>
            )}

            {/* Rich HTML preview for styles, alignments, headings, tables, signatures */}
            {item.sampleOutput.type === 'styled_text' && (
              <div className="syntax-output-document-sheet">
                <div className="syntax-output-sheet-header">
                  <span className="syntax-output-sheet-title">
                    {item.sampleOutput.title || 'GENERATED OUTPUT'}
                  </span>
                </div>
                <div
                  className="syntax-output-sheet-body"
                  dangerouslySetInnerHTML={{ __html: item.sampleOutput.htmlContent }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Common Variables Reference (for Variables section) ── */}
      {item.commonExamples && item.commonExamples.length > 0 && (
        <div className="syntax-sub-section">
          <span className="syntax-sub-label">COMMON NOTICE VARIABLES</span>
          <div className="syntax-common-vars-grid">
            {item.commonExamples.map((ex, idx) => (
              <div key={idx} className="syntax-common-var-chip">
                <code className="syntax-common-var-name">{ex.name}</code>
                <span className="syntax-common-var-arrow">→</span>
                <span className="syntax-common-var-val">{ex.output}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Notes / Rules Section ── */}
      {item.notes && (
        <div className="syntax-rules-callout">
          <div className="syntax-rules-callout-icon">
            <Info size={15} />
          </div>
          <div className="syntax-rules-callout-text">
            <strong>Notes & Rules: </strong>
            <span>{item.notes}</span>
          </div>
        </div>
      )}
    </article>
  );
}
